import { GRID_TREE_DATA_GROUPING_FIELD, GridColDef, useGridApiRef } from '@mui/x-data-grid-pro';
import React, { useMemo } from 'react';
import {
  outputOf,
  sampleId,
  stGridSampleAvailability,
  stGridSampleSampleTypeId,
  stGridTransitionSampleTransitionId,
} from '../../util/Constants';
import { Sample } from '../../data/SampleData';
import { SampleTransitionInformation, SampleHierarchyTree } from '../../data/SampleHierarchyData';
import { makeStyles } from '@mui/styles';
import { keyBy } from 'lodash';
import useMemoTranslation from '../../hooks/UseMemoTranslation';
import { Box } from '@mui/material';
import { GridCellCallBack } from '../../components/grid/GridCellCallback';
import { Dictionary, UseStateSetter } from '../../util/TypeUtil';
import { sampleTrackingGridDefaultColumnOverrides } from '../../util/grid/TableUtils';
import { SampleTrackingGridWrapper } from '../../components/grid/SampleTrackingGridWrapper';
import { LoadingProps } from '../../components/LoadingStateUtil';
import { ConfiguredTransition } from '../../data/ConfiguredTransitionData';
import { TextOverflowBox } from 'components/TextOverflowBox';
import { useSampleTrackingConfig } from '../../contexts/SampleTrackingConfigContext';

export interface SampleHierarchyGridProps {
  identifiersBySampleId: Dictionary<string>;
  transitionInformation: ReadonlyArray<SampleTransitionInformation>;
  hierarchyTreeData: ReadonlyArray<SampleHierarchyTree>;
  selectedSampleIds: ReadonlyArray<string>;
  setSelectedSampleIds: UseStateSetter<ReadonlyArray<string>>;
  configuredTransitions: ReadonlyArray<ConfiguredTransition>;
  loadingProps: LoadingProps;
  rightActions?: React.ReactNode;
  leftActions?: React.ReactNode;
  completedSampleIds: ReadonlyArray<string>;
}

export type Row = Omit<SampleHierarchyTree, 'descendant'> & {
  id: number;
  path: string[];
  checkByStatus?: string;
  outputOf: string;
  currentStage: string;
} & Sample &
  SampleTransitionInformation;

const useStyles = makeStyles({
  highlightedRow: {
    backgroundColor: 'rgb(231, 221, 255)',
  },
  noHover: {
    '&:hover': {
      backgroundColor: 'inherit !important',
    },
    '& .MuiDataGrid-cell': {
      cursor: 'default',
    },
  },
  completedRow: {
    backgroundColor: '#e3f2fd',
    '&:hover': {
      backgroundColor: '#e3f2fd !important',
    },
  },
});

/** Unique identifier for this component. Used for view management persistence. Should not be changed once set. */
const COMPONENT_NAME = 'SampleHierarchyGrid';

export const SampleHierarchyGrid = ({
  identifiersBySampleId,
  transitionInformation,
  hierarchyTreeData,
  selectedSampleIds,
  setSelectedSampleIds,
  configuredTransitions,
  loadingProps,
  leftActions,
  rightActions,
  completedSampleIds,
}: SampleHierarchyGridProps) => {
  const apiRef = useGridApiRef();
  const { t } = useMemoTranslation();
  const classes = useStyles();
  const columns = useColumns();
  const rows = useRows(hierarchyTreeData, transitionInformation, configuredTransitions);
  const { configuredTransitionId, transitionEnumId, researchProjectId } = useSampleTrackingConfig();

  return (
    <SampleTrackingGridWrapper
      apiRef={apiRef}
      rows={rows}
      columns={columns}
      initialState={{
        sorting: {
          sortModel: [{ field: sampleId, sort: 'asc' }],
        },
      }}
      getTreeDataPath={(row: Row) => row?.path}
      treeData
      groupingColDef={{
        headerAlign: 'left',
        headerName: t('sampleIdentifier'),
        minWidth: 500,
        hideDescendantCount: true,
        cellClassName: 'monospace-font',
        renderCell: params => {
          const { row, rowNode, value } = params;
          const sampleId = row.sampleId;

          return (
            <Box sx={{ ml: rowNode.depth * 4, fontFamily: 'monospace', width: 'inherit' }}>
              {selectedSampleIds.includes(sampleId) ? (
                <TextOverflowBox value={value} />
              ) : (
                <GridCellCallBack
                  text={value}
                  callBack={() => {
                    setSelectedSampleIds([sampleId]);
                  }}
                  sx={{ paddingLeft: 0, minWidth: 0, fontFamily: 'monospace' }}
                />
              )}
            </Box>
          );
        },
        valueGetter: params => identifiersBySampleId[params?.row?.sampleId ?? ''] ?? '',
      }}
      defaultGroupingExpansionDepth={100}
      disableRowSelectionOnClick
      getRowClassName={params => {
        if (completedSampleIds && completedSampleIds.includes(params.row.sampleId)) {
          return classes.completedRow;
        } else if (selectedSampleIds && selectedSampleIds.includes(params.row.sampleId)) {
          return classes.highlightedRow;
        }
        return classes.noHover;
      }}
      leftActions={leftActions}
      rightActions={rightActions}
      loadingProps={loadingProps}
      hideLoadingIndicator
      defaultColumnOrdering={[
        GRID_TREE_DATA_GROUPING_FIELD,
        outputOf,
        stGridTransitionSampleTransitionId,
        stGridSampleSampleTypeId,
        stGridSampleAvailability,
      ]}
      cacheKey={'sample-tracking-hierarchy-grid'}
      columnDefinitionOverrides={sampleTrackingGridDefaultColumnOverrides}
      componentName={COMPONENT_NAME}
      configuredTransitionId={configuredTransitionId}
      transitionEnumId={transitionEnumId}
      researchProjectId={researchProjectId}
    />
  );
};

const useRows = (
  trees: ReadonlyArray<SampleHierarchyTree>,
  transitionInformation: ReadonlyArray<SampleTransitionInformation>,
  configuredTransitions: ReadonlyArray<ConfiguredTransition>
): ReadonlyArray<Row> => {
  return useMemo(() => {
    const informationBySampleId = keyBy(transitionInformation, t => t.sampleId);
    const transitionById = keyBy(configuredTransitions, i => i.configuredTransitionId);

    let id = 0;

    function getNodes(tree: SampleHierarchyTree, ancestorPath: string[] = []) {
      const information = informationBySampleId[tree.ancestor.sampleId] ?? {};
      const currentId = id++;
      const uniqueNodeId = `${tree.ancestor.sampleId}_${currentId}`;
      const currentPath = [...ancestorPath, uniqueNodeId];

      const nodes: Row[] = [
        {
          id: currentId,
          ancestor: tree.ancestor,
          depth: tree.depth,
          path: currentPath,
          isLeaf: tree.isLeaf,
          ...tree.ancestor,
          ...information,
          outputOf: transitionById[information?.transitionCreatedIn?.configuredTransitionId ?? '']?.displayName,
          currentStage: transitionById[information?.currentTransition?.configuredTransitionId ?? '']?.displayName,
        },
      ];

      for (const child of tree.descendant) {
        nodes.push(...getNodes(child, currentPath));
      }

      return nodes;
    }

    return trees.flatMap(tree => getNodes(tree));
  }, [trees, transitionInformation, configuredTransitions]);
};

const useColumns = (): GridColDef[] => {
  const { t } = useMemoTranslation();

  return useMemo(
    () => [
      {
        field: outputOf,
        headerName: t(outputOf),
        headerAlign: 'left',
        align: 'left',
        width: 150,
        valueFormatter: ({ value }) => value && t(value),
      },
    ],
    [t]
  );
};
